import { useEffect, useCallback } from 'react';
import { api } from '../lib/api-client';
import { useNotifications } from '../contexts/NotificationContext';

/**
 * Custom hook for triggering AI configuration health check on backend.
 * Backend creates notifications when issues are found.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.checkOnMount - Whether to check health on mount (default: true)
 * @returns {Object} AI health functions
 */
export function useAIHealth({ checkOnMount = true } = {}) {
  const { fetchNotifications } = useNotifications();

  /**
   * Trigger backend AI health check
   */
  const checkAIHealth = useCallback(async () => {
    try {
      // Trigger backend health check (backend creates notifications if needed)
      await api.post('/api/v1/admin/ai/model-configurations/health-check');

      // Refresh unread notifications to pick up any newly created notifications
      await fetchNotifications({ status: 'unread', limit: 50 });

      return true;
    } catch (error) {
      console.error('[useAIHealth] Failed to trigger health check:', error);
      return false;
    }
  }, [fetchNotifications]);

  // Trigger health check on mount if requested
  useEffect(() => {
    if (checkOnMount) {
      checkAIHealth();
    }
  }, [checkOnMount, checkAIHealth]);

  return {
    checkAIHealth
  };
}
